// TableCard.tsx
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  styled,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { ITables, KITCHEN_ITEM_STATUS, ORDER_TYPE } from '../../../all-types';
import { getKitchenItemStatusInfo } from '../../../utils';
import { useAppDispatch } from '../../../redux-store/hooks';
import { updateTable } from '../../../redux-store';
import { useTableByIdLive } from '../../../realtime';
import { useEffect, useState } from 'react';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.8rem',
  height: 30,
  width: 110,
  fontWeight: 700,
  textTransform: 'capitalize',
  borderRadius: 8,
}));

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 500,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

const getTimeFromValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatElapsedTime = (startedAt?: number | null, now = Date.now(), endedAt?: number | null) => {
  if (!startedAt) return '-';

  const end = endedAt || now;
  const totalMinutes = Math.max(0, Math.floor((end - startedAt) / 60000));
  if (totalMinutes < 1) return 'Now';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

const getItemStartedAt = (item: any) =>
  getTimeFromValue(item?.kitchInfo?.itemReceviedTime) ||
  getTimeFromValue(item?.kitchInfo?.itemReceivedTime) ||
  getTimeFromValue(item?.createdAt);

const getItemEndedAt = (item: any) => {
  if (item?.kitchInfo?.status !== KITCHEN_ITEM_STATUS.SERVED) return null;
  return (
    getTimeFromValue(item?.kitchInfo?.itemServedTime) ||
    getTimeFromValue(item?.kitchInfo?.updatedAt)
  );
};

const getTableOccupiedAt = (items: any[]) => {
  const itemTimes = items
    .map(getItemStartedAt)
    .filter((time): time is number => Number.isFinite(time));

  if (!itemTimes.length) return null;
  return Math.min(...itemTimes);
};

type TableCardProps = {
  table: ITables;
  isSelected?: boolean;
};

function FullMenuTableCard({ table, isSelected = false }: TableCardProps) {
  const theme = useTheme();
  const dispatcher = useAppDispatch();
  const [now, setNow] = useState(() => Date.now());

  useTableByIdLive(table._id, (t) => dispatcher(updateTable(t)));

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable?.main || theme.palette.success.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#E33201';
      default:
        return theme.palette.text.primary;
    }
  };

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8';
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB';
      default:
        return theme.palette.divider;
    }
  };

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8?.main || theme.palette.background.paper;
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED';
      default:
        return theme.palette.background.paper;
    }
  };

  const getInstructions = (inst?: string) => {
    if (!inst) return '';
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst);
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      );
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  const occupiedAt = getTableOccupiedAt(table.billSession?.items || []);
  const tableElapsedLabel = formatElapsedTime(occupiedAt, now);

  if (!table?.billSession?.items?.length) return null;

  const allItemsServed = table.billSession.items.every(
    (item) => item.kitchInfo?.status === KITCHEN_ITEM_STATUS.SERVED
  );
  const orderCompletedLabel = allItemsServed ? 'Completed' : 'In progress';
  const cardAccentColor = allItemsServed ? '#81C784' : '#FFCC80';
  const cardAccentTextColor = allItemsServed ? '#2E7D32' : '#A15C00';
  const cardAccentBgColor = allItemsServed ? '#F1F8E9' : '#FFF8E1';

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          backgroundColor: cardAccentBgColor,
          '&:hover': { boxShadow: 6 },
          borderRadius: 3,
          border: `1px solid ${cardAccentColor}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 5, bgcolor: cardAccentColor }} />
        <CardContent sx={{ flexGrow: 1, pb: 1.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Table No #{table?.tableNo}
              </Typography>
              <Typography variant="body2" fontWeight={800} color="text.secondary">
                {orderCompletedLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  px: 1.75,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  color: cardAccentTextColor,
                  border: `1px solid ${cardAccentColor}`,
                  minWidth: 112,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" fontWeight={900} lineHeight={1.1} sx={{ display: 'block' }}>
                  Occupied
                </Typography>
                <Typography variant="h6" fontWeight={900} lineHeight={1.2}>
                  {tableElapsedLabel}
                </Typography>
              </Box>
              {/* <StatusChip
                label={orderCompletedLabel}
                size="small"
                sx={{
                  bgcolor: 'background.paper',
                  color: cardAccentTextColor,
                  border: `1px solid ${cardAccentColor}`,
                  fontSize: '0.78rem',
                }}
              /> */}
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Column headers */}
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={5}>
              <Typography variant="body2" fontWeight="bold">Item Name</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2" fontWeight="bold">Qty</Typography>
            </Grid>
            <Grid item xs={3} justifyContent="flex-end" display="flex">
              <Typography variant="body2" fontWeight="bold">Status</Typography>
            </Grid>
            <Grid item xs={2} justifyContent="flex-end" display="flex">
              <Typography variant="body2" fontWeight="bold">Time</Typography>
            </Grid>
          </Grid>

          {/* Items list (scroll-safe) */}
          <Box sx={{ maxHeight: 280, pr: 0.5, overflowY: 'auto' }}>
            {table.billSession.items.map((item, idx) => {
              const rowKey = item?.sno ||  `${table._id}-${idx}`;
              const statusInfo = getKitchenItemStatusInfo(item?.kitchInfo?.status || KITCHEN_ITEM_STATUS.IN_KITCHEN);
              const itemStartedAt = getItemStartedAt(item);
              const itemEndedAt = getItemEndedAt(item);
              const itemElapsedLabel = formatElapsedTime(itemStartedAt, now, itemEndedAt);
              const isServed = item?.kitchInfo?.status === KITCHEN_ITEM_STATUS.SERVED;
              return (
                <Box key={rowKey} sx={{ mb: 1 }}>
                  <Grid container spacing={1} alignItems="flex-start">
                    <Grid item xs={5}>
                      <Typography variant="body1" fontWeight={800} sx={{ textTransform: 'capitalize' }}>
                        {item.itemName}
                      </Typography>

                      {!!item?.kitchInfo?.kitchenInstrcutions && (
                        <Typography
                          sx={{ display: 'block', mt: 0.5, fontSize: 12, color: 'text.secondary', fontWeight: 700 }}
                        >
                          {getInstructions(item.kitchInfo.kitchenInstrcutions)}
                        </Typography>
                      )}

                      <Chip
                        label={`${item.quantity <= 1 ? '' : item.quantity} ${item.quantityType || ''}`.trim()}
                        sx={{ mt: 0.75, fontSize: '0.6rem', height: 24, borderRadius: 1.5 }}
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
                      <Typography variant="subtitle1" fontWeight={900}>x{item.noOfItems}</Typography>
                    </Grid>

                    <Grid item xs={3} display="flex" justifyContent="flex-end">
                      <StatusChip
                        size="small"
                        variant="outlined"
                        label={statusInfo.displayText}
                        color={statusInfo.color as any}
                        sx={{ width: 'auto', px: 1.25, height: 26 }}
                      />
                    </Grid>

                    <Grid item xs={2} display="flex" justifyContent="flex-end">
                      {!isServed && (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.6,
                            minWidth: 64,
                            borderRadius: 1.5,
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: (t) => `1px solid ${t.palette.divider}`,
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="body2" fontWeight={900} lineHeight={1}>
                            {itemElapsedLabel}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
          </Box>
        </CardContent>

        {/* Footer badge */}
        <OrderTypeBadge
          sx={{
            bgcolor: getTypeColor(ORDER_TYPE.DINE_IN),
            color: getTypeTextColor(ORDER_TYPE.DINE_IN),
            borderTop: `1px dashed ${getTypeBorderColor(ORDER_TYPE.DINE_IN)}`,
          }}
        >
          <Typography variant="caption" textTransform="capitalize" fontWeight={700}>
            {ORDER_TYPE.DINE_IN.toLowerCase()}
          </Typography>
          <StatusChip
            label={orderCompletedLabel}
            size="small"
            sx={{
              width: 'auto',
              bgcolor: 'background.paper',
              color: cardAccentTextColor,
              border: `1px solid ${cardAccentColor}`,
              height: 24,
              fontSize: '0.72rem',
            }}
          />
        </OrderTypeBadge>
      </Card>
    </Grid>
  );
}

export { FullMenuTableCard };
